// src/components/sports/MatchCard.jsx - Updated to show games without odds

import React, { useState } from 'react';
import BetButton from '../common/BetButton';

const MatchCard = ({ match, sport, oddsFormat, viewMode = 'grid', onAddToBetslip }) => {
  const [activeMarket, setActiveMarket] = useState('moneyline');
  
  // Football statuses
  const footballLiveStatuses = ['1H', 'HT', '2H', 'ET', 'P'];
  
  // Basketball statuses
  const basketballLiveStatuses = ['1Q', '2Q', 'HT', '3Q', '4Q', 'OT'];
  
  // Determine if match is live based on sport
  const isLive = sport === 'basketball' 
    ? basketballLiveStatuses.includes(match.status)
    : footballLiveStatuses.includes(match.status);
  
  const isCompact = viewMode === 'grid';
  
  // Format date/time
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (date - now) / (1000 * 60 * 60);
    
    if (diffHours < 0 && isLive) {
      return 'LIVE';
    } else if (diffHours < 0) {
      return 'Finished';
    } else if (diffHours < 1) {
      const minutes = Math.floor(diffHours * 60);
      return `${minutes}m`;
    } else if (diffHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Get basketball status display
  const getBasketballStatusText = (status) => {
    const statusMap = {
      'NS': 'Not Started',
      '1Q': '1st Q',
      '2Q': '2nd Q',
      'HT': 'Half Time',
      '3Q': '3rd Q',
      '4Q': '4th Q',
      'OT': 'Overtime',
      'FT': 'Finished',
      'AOT': 'After OT',
      'CANC': 'Cancelled',
      'PST': 'Postponed'
    };
    return statusMap[status] || status;
  };

  // Format elapsed time for basketball
  const formatBasketballElapsed = (status, elapsed) => {
    if (!elapsed) return getBasketballStatusText(status);
    return `${status} ${elapsed}`;
  };

  // Check if odds exist - MODIFIED to be more flexible
  const hasOdds = match.odds && (
    (match.odds.moneyline?.home !== null && match.odds.moneyline?.away !== null) ||
    (match.odds.home !== null && match.odds.away !== null) ||
    match.odds.draw !== null ||
    match.odds.point_spread?.home !== null ||
    match.odds.total_points?.over !== null
  );

  // Check if we have at least some betting data
  const hasAnyOdds = match.odds && Object.keys(match.odds).length > 0;

  // Don't hide matches without odds - show them with "Odds coming soon" message
  // Only hide if we're completely missing the match data
  if (!match || !match.id) {
    return null;
  }

  // Render football odds (1X2)
  const renderFootballOdds = () => {
    const odds = match.odds || {};
    const homeOdds = odds.home ? parseFloat(odds.home) : null;
    const drawOdds = odds.draw ? parseFloat(odds.draw) : null;
    const awayOdds = odds.away ? parseFloat(odds.away) : null;
    
    const hasHomeOdds = homeOdds !== null && homeOdds > 0;
    const hasDrawOdds = drawOdds !== null && drawOdds > 0;
    const hasAwayOdds = awayOdds !== null && awayOdds > 0;

    // If no odds at all, show "Odds coming soon"
    if (!hasHomeOdds && !hasDrawOdds && !hasAwayOdds) {
      return (
        <div className="text-center py-3">
          <span className="text-xs text-gray-500">Odds coming soon</span>
        </div>
      );
    }

    return (
      <div className="flex space-x-1 sm:space-x-2">
        <BetButton
          odds={homeOdds}
          oddsFormat={oddsFormat}
          selection="1"
          selectionName={match.home_team}
          match={`${match.home_team} vs ${match.away_team}`}
          matchId={match.id}
          sport="football"
          type="home"
          isCompact={isCompact}
          disabled={!hasHomeOdds}
          onAddToBetslip={onAddToBetslip}
        />
        
        <BetButton
          odds={drawOdds}
          oddsFormat={oddsFormat}
          selection="X"
          selectionName="Draw"
          match={`${match.home_team} vs ${match.away_team}`}
          matchId={match.id}
          sport="football"
          type="draw"
          isCompact={isCompact}
          disabled={!hasDrawOdds}
          onAddToBetslip={onAddToBetslip}
        />
        
        <BetButton
          odds={awayOdds}
          oddsFormat={oddsFormat}
          selection="2"
          selectionName={match.away_team}
          match={`${match.home_team} vs ${match.away_team}`}
          matchId={match.id}
          sport="football"
          type="away"
          isCompact={isCompact}
          disabled={!hasAwayOdds}
          onAddToBetslip={onAddToBetslip}
        />
      </div>
    );
  };

  // Render basketball odds with market tabs
  const renderBasketballOdds = () => {
    const odds = match.odds || {};
    
    // Moneyline
    const homeML = odds.moneyline?.home ? parseFloat(odds.moneyline.home) : null;
    const awayML = odds.moneyline?.away ? parseFloat(odds.moneyline.away) : null;
    const hasML = homeML !== null && homeML > 0 && awayML !== null && awayML > 0;

    // Point Spread
    const homeSpread = odds.point_spread?.home ? parseFloat(odds.point_spread.home) : null;
    const awaySpread = odds.point_spread?.away ? parseFloat(odds.point_spread.away) : null;
    const spreadValue = odds.point_spread?.value ? parseFloat(odds.point_spread.value) : null;
    const hasSpread = homeSpread !== null && homeSpread > 0 && awaySpread !== null && awaySpread > 0;

    // Total Points
    const overOdds = odds.total_points?.over ? parseFloat(odds.total_points.over) : null;
    const underOdds = odds.total_points?.under ? parseFloat(odds.total_points.under) : null;
    const totalValue = odds.total_points?.value ? parseFloat(odds.total_points.value) : null;
    const hasTotal = overOdds !== null && overOdds > 0 && underOdds !== null && underOdds > 0;

    // Check if we have any odds at all
    const hasAnyBasketballOdds = hasML || hasSpread || hasTotal;

    // If no basketball-specific odds, try the old format
    if (!hasAnyBasketballOdds) {
      const homeOdds = odds.home ? parseFloat(odds.home) : null;
      const awayOdds = odds.away ? parseFloat(odds.away) : null;
      
      if (homeOdds && awayOdds) {
        return (
          <div className="flex space-x-1 sm:space-x-2">
            <BetButton
              odds={homeOdds}
              oddsFormat={oddsFormat}
              selection="home"
              selectionName={match.home_team}
              match={`${match.home_team} vs ${match.away_team}`}
              matchId={match.id}
              sport="basketball"
              type="home"
              isCompact={isCompact}
              disabled={false}
              onAddToBetslip={onAddToBetslip}
            />
            <BetButton
              odds={awayOdds}
              oddsFormat={oddsFormat}
              selection="away"
              selectionName={match.away_team}
              match={`${match.home_team} vs ${match.away_team}`}
              matchId={match.id}
              sport="basketball"
              type="away"
              isCompact={isCompact}
              disabled={false}
              onAddToBetslip={onAddToBetslip}
            />
          </div>
        );
      }
      
      // No odds at all - show "Odds coming soon"
      return (
        <div className="text-center py-3">
          <span className="text-xs text-gray-500">Odds coming soon</span>
        </div>
      );
    }

    // Get market label
    const getMarketLabel = () => {
      switch(activeMarket) {
        case 'moneyline':
          return 'Moneyline';
        case 'spread':
          return `Spread ${spreadValue ? (spreadValue > 0 ? '+' : '') + spreadValue : ''}`;
        case 'total':
          return `Total ${totalValue || ''}`;
        default:
          return 'Moneyline';
      }
    };

    // Render selected market
    const renderMarket = () => {
      switch(activeMarket) {
        case 'moneyline':
          return (
            <div className="flex space-x-1 sm:space-x-2">
              <BetButton
                odds={homeML}
                oddsFormat={oddsFormat}
                selection="home"
                selectionName={match.home_team}
                match={`${match.home_team} vs ${match.away_team}`}
                matchId={match.id}
                sport="basketball"
                type="home"
                isCompact={isCompact}
                disabled={!hasML}
                onAddToBetslip={onAddToBetslip}
              />
              <BetButton
                odds={awayML}
                oddsFormat={oddsFormat}
                selection="away"
                selectionName={match.away_team}
                match={`${match.home_team} vs ${match.away_team}`}
                matchId={match.id}
                sport="basketball"
                type="away"
                isCompact={isCompact}
                disabled={!hasML}
                onAddToBetslip={onAddToBetslip}
              />
            </div>
          );

        case 'spread':
          return (
            <div className="flex space-x-1 sm:space-x-2">
              <BetButton
                odds={homeSpread}
                oddsFormat={oddsFormat}
                selection="spread_home"
                selectionName={`${match.home_team} ${spreadValue ? (spreadValue > 0 ? '+' : '') + spreadValue : ''}`}
                match={`${match.home_team} vs ${match.away_team}`}
                matchId={match.id}
                sport="basketball"
                type="spread_home"
                isCompact={isCompact}
                disabled={!hasSpread}
                onAddToBetslip={onAddToBetslip}
              />
              <BetButton
                odds={awaySpread}
                oddsFormat={oddsFormat}
                selection="spread_away"
                selectionName={`${match.away_team} ${spreadValue ? (spreadValue > 0 ? '-' : '+') + Math.abs(spreadValue) : ''}`}
                match={`${match.home_team} vs ${match.away_team}`}
                matchId={match.id}
                sport="basketball"
                type="spread_away"
                isCompact={isCompact}
                disabled={!hasSpread}
                onAddToBetslip={onAddToBetslip}
              />
            </div>
          );

        case 'total':
          return (
            <div className="flex space-x-1 sm:space-x-2">
              <BetButton
                odds={overOdds}
                oddsFormat={oddsFormat}
                selection="over"
                selectionName={`Over ${totalValue || ''}`}
                match={`${match.home_team} vs ${match.away_team}`}
                matchId={match.id}
                sport="basketball"
                type="over"
                isCompact={isCompact}
                disabled={!hasTotal}
                onAddToBetslip={onAddToBetslip}
              />
              <BetButton
                odds={underOdds}
                oddsFormat={oddsFormat}
                selection="under"
                selectionName={`Under ${totalValue || ''}`}
                match={`${match.home_team} vs ${match.away_team}`}
                matchId={match.id}
                sport="basketball"
                type="under"
                isCompact={isCompact}
                disabled={!hasTotal}
                onAddToBetslip={onAddToBetslip}
              />
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="space-y-2">
        {/* Market Tabs - Only show if we have multiple markets */}
        {(hasML && hasSpread) || (hasML && hasTotal) || (hasSpread && hasTotal) ? (
          <div className="flex space-x-1">
            {hasML && (
              <button
                onClick={() => setActiveMarket('moneyline')}
                className={`px-2 py-0.5 text-xs rounded ${
                  activeMarket === 'moneyline' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                ML
              </button>
            )}
            {hasSpread && (
              <button
                onClick={() => setActiveMarket('spread')}
                className={`px-2 py-0.5 text-xs rounded ${
                  activeMarket === 'spread' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                Spread
              </button>
            )}
            {hasTotal && (
              <button
                onClick={() => setActiveMarket('total')}
                className={`px-2 py-0.5 text-xs rounded ${
                  activeMarket === 'total' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                Total
              </button>
            )}
            <span className="ml-auto text-[10px] text-gray-500">{getMarketLabel()}</span>
          </div>
        ) : (
          // Single market - just show the label
          <div className="flex justify-end">
            <span className="text-[10px] text-gray-500">{getMarketLabel()}</span>
          </div>
        )}
        
        {/* Market Odds */}
        {renderMarket()}
      </div>
    );
  };

  // Get time display
  const getTimeDisplay = () => {
    if (sport === 'basketball') {
      if (isLive && match.elapsed) {
        return formatBasketballElapsed(match.status, match.elapsed);
      }
      if (isLive && !match.elapsed) {
        return getBasketballStatusText(match.status);
      }
      return formatDate(match.fixture_date);
    }
    return formatDate(match.fixture_date);
  };

  // Get status color
  const getStatusColor = () => {
    if (sport === 'basketball') {
      const liveStatuses = ['1Q', '2Q', 'HT', '3Q', '4Q', 'OT'];
      if (liveStatuses.includes(match.status)) {
        return 'text-red-400 font-semibold animate-pulse';
      }
      if (match.status === 'FT' || match.status === 'AOT') {
        return 'text-gray-500';
      }
      return 'text-gray-400';
    }
    return isLive ? 'text-red-400 font-semibold' : 'text-gray-400';
  };

  // Determine if we should show the match (always show, even without odds)
  // Only hide if status is cancelled or postponed
  if (match.status === 'CANC' || match.status === 'PST') {
    return null;
  }

  return (
    <div className={`bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors duration-200 ${
      isCompact ? 'p-3 sm:p-4' : 'p-4 sm:p-6'
    }`}>
      {/* Match Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          {match.league_logo && (
            <img src={match.league_logo} alt={match.league_name} className="w-4 h-4 object-contain" />
          )}
          <div className="text-xs text-gray-400 truncate">{match.league_name}</div>
          {sport === 'basketball' && match.league_name?.toLowerCase().includes('wnba') && (
            <span className="text-[10px] bg-purple-700 text-white px-1.5 py-0.5 rounded">WNBA</span>
          )}
        </div>
        <div className={`text-xs whitespace-nowrap ${getStatusColor()}`}>
          {getTimeDisplay()}
        </div>
      </div>
      
      {/* Live Score Display */}
      {isLive && (match.home_score !== null || match.away_score !== null) && (
        <div className="flex justify-center gap-4 sm:gap-6 mb-4 pb-2 border-b border-gray-800">
          <div className="text-center">
            <div className="text-xs text-gray-400 truncate max-w-[60px] sm:max-w-[80px]">{match.home_team}</div>
            <div className="text-2xl font-bold text-white">{match.home_score ?? 0}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400">VS</div>
            <div className="text-xl font-bold text-gray-400">-</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-400 truncate max-w-[60px] sm:max-w-[80px]">{match.away_team}</div>
            <div className="text-2xl font-bold text-white">{match.away_score ?? 0}</div>
          </div>
        </div>
      )}
      
      {/* Teams with Logos */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {match.home_team_logo && (
            <img src={match.home_team_logo} alt={match.home_team} className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0" />
          )}
          <div className={`font-medium text-white truncate ${isCompact ? 'text-sm' : 'text-base'}`}>
            {match.home_team}
          </div>
        </div>
        
        <div className={`px-2 text-gray-400 flex-shrink-0 ${isCompact ? 'text-xs' : 'text-sm'}`}>VS</div>
        
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <div className={`font-medium text-white truncate text-right ${isCompact ? 'text-sm' : 'text-base'}`}>
            {match.away_team}
          </div>
          {match.away_team_logo && (
            <img src={match.away_team_logo} alt={match.away_team} className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0" />
          )}
        </div>
      </div>
      
      {/* Betting Odds - Sport Specific */}
      {sport === 'basketball' ? renderBasketballOdds() : renderFootballOdds()}
    </div>
  );
};

export default MatchCard;